import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SofaIcon, BedIcon, UtensilsIcon, BathIcon, ZapIcon, ThermometerIcon, SmileIcon } from 'lucide-react';
// Components
import { SystemStatusBar } from './components/SystemStatusBar';
import { RoomZonesSidebar } from './components/RoomZonesSidebar';
import { FloorPlanHeatMap } from './components/FloorPlanHeatMap';
import { EnergyChart } from './components/EnergyChart';
import { MonthlyEnergyWidget } from './components/MonthlyEnergyWidget';
import { TopOutletWidget } from './components/TopOutletWidget';
import { OutletPowerChart } from './components/OutletPowerChart';
import { ZoneDetailSidebar } from './components/ZoneDetailSidebar';
import { SensorDetailSidebar } from './components/SensorDetailSidebar';
import { Sensor, ViewMode } from './components/FloorPlanHeatMap';
// Types
type Mode = 'cool' | 'heat' | 'auto' | 'fan' | 'off';
const ROOMS = [{
  id: 'living',
  name: '거실',
  temp: 24,
  icon: SofaIcon
}, {
  id: 'bedroom',
  name: '침실',
  temp: 27,
  icon: BedIcon
}, {
  id: 'kitchen',
  name: '주방',
  temp: 22,
  icon: UtensilsIcon
}, {
  id: 'bathroom',
  name: '화장실',
  temp: 21,
  icon: BathIcon
}];
const VIEW_MODES: {
  id: ViewMode;
  label: string;
  icon: any;
  color: string;
  activeColor: string;
}[] = [{
  id: 'energy',
  label: '에너지',
  icon: ZapIcon,
  color: 'text-slate-400',
  activeColor: 'text-amber-400'
}, {
  id: 'temperature',
  label: '온도',
  icon: ThermometerIcon,
  color: 'text-slate-400',
  activeColor: 'text-cyan-400'
}, {
  id: 'comfort',
  label: '쾌적성',
  icon: SmileIcon,
  color: 'text-slate-400',
  activeColor: 'text-emerald-400'
}];
export function App() {
  const [selectedRoomId, setSelectedRoomId] = useState('living');
  const [mode, setMode] = useState<Mode>('cool');
  const [viewMode, setViewMode] = useState<ViewMode>('temperature');
  const [targetTemp, setTargetTemp] = useState(70);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const currentRoom = ROOMS.find(r => r.id === selectedRoomId) || ROOMS[0];
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);
  const [displayTemp, setDisplayTemp] = useState(currentRoom.temp);
  useEffect(() => {
    const interval = setInterval(() => {
      const variance = Math.random() > 0.5 ? 0.1 : -0.1;
      if (Math.abs(displayTemp - currentRoom.temp) < 1) {
        setDisplayTemp(prev => Number((prev + variance).toFixed(1)));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [currentRoom, displayTemp]);
  useEffect(() => {
    setDisplayTemp(currentRoom.temp);
  }, [selectedRoomId]);
  const handleRoomSelect = (id: string) => {
    setSelectedRoomId(id);
    setIsZoomed(true);
    setSelectedSensor(null);
  };
  const handleZoomOut = () => {
    setIsZoomed(false);
    setSelectedSensor(null);
  };
  const handleSensorSelect = (sensor: Sensor) => {
    setSelectedSensor(sensor);
  };
  const handleSensorBack = () => {
    setSelectedSensor(null);
  };
  return <div className="h-screen bg-[#0a0f1a] text-slate-200 font-sans selection:bg-cyan-500/30 flex flex-col overflow-hidden" data-id="element-0">
      {/* Background Grid Effect */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-20" style={{
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }} data-id="element-1" />
      <div className="fixed inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" data-id="element-2" />

      <SystemStatusBar data-id="element-3" />

      <div className="flex-1 flex flex-row overflow-hidden relative z-10 min-h-0" data-id="element-4">
        {/* Left Sidebar */}
        <RoomZonesSidebar selectedRoom={selectedRoomId} onSelectRoom={handleRoomSelect} rooms={ROOMS} humidity={48} aqi={35} data-id="element-5" />

        {/* Main Content Area */}
        <main className="flex-1 flex flex-col p-3 overflow-hidden min-w-0" data-id="element-6">
          <div className="w-full h-full flex flex-col min-h-0" data-id="element-7">
            {/* Header */}
            <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-2 flex items-center justify-between flex-shrink-0" data-id="element-8">
              <div data-id="element-9">
                <h1 className="text-2xl font-bold text-white tracking-tight mb-0.5" data-id="element-10">
                  {currentRoom.name}
                </h1>
                <div className="flex items-center space-x-2 text-slate-400 text-xs" data-id="element-11">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" data-id="element-12" />
                  <span data-id="element-13">쾌적성 자동 조절 중</span>
                </div>
              </div>

              {/* View Mode Toggle Buttons */}
              <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1 border border-white/5" data-id="element-14">
                {VIEW_MODES.map(vm => {
                const isActive = viewMode === vm.id;
                const Icon = vm.icon;
                return <button key={vm.id} onClick={() => setViewMode(vm.id)} className={`
                        relative flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-300
                        ${isActive ? 'bg-white/10 shadow-sm ' + vm.activeColor : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}
                      `} data-id="element-15">
                      {isActive && <motion.div layoutId="viewModeIndicator" className="absolute inset-0 bg-white/10 rounded-lg" transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30
                  }} data-id="element-16" />}
                      <Icon className="w-3.5 h-3.5 relative z-10" data-id="element-17" />
                      <span className="relative z-10" data-id="element-18">{vm.label}</span>
                    </button>;
              })}
              </div>
            </motion.div>

            {/* Floor Plan Heat Map */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="flex-1 flex flex-col items-center glass-panel rounded-2xl p-3 relative overflow-hidden min-h-0" data-id="element-19">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" data-id="element-20" />
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" data-id="element-21" />

              <div className="relative z-10 flex flex-col items-center w-full h-full min-h-0" data-id="element-22">
                <div className="flex-1 w-full flex items-center justify-center overflow-hidden min-h-0" data-id="element-23">
                  <FloorPlanHeatMap rooms={ROOMS} selectedRoomId={selectedRoomId} onSelectRoom={handleRoomSelect} isZoomed={isZoomed} onZoomOut={handleZoomOut} onSelectSensor={handleSensorSelect} selectedSensorId={selectedSensor?.id} viewMode={viewMode} data-id="element-24" />
                </div>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Right Sidebar */}
        <AnimatePresence mode="wait" data-id="element-25">
          {selectedSensor ? <motion.div key="sensor-detail" initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 50
        }} transition={{
          duration: 0.3
        }} className="h-full" data-id="element-26">
              <SensorDetailSidebar sensor={selectedSensor} onBack={handleSensorBack} onClose={handleZoomOut} data-id="element-27" />
            </motion.div> : isZoomed ? <motion.div key="zone-detail" initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 50
        }} transition={{
          duration: 0.3
        }} className="h-full" data-id="element-28">
              <ZoneDetailSidebar room={currentRoom} onClose={handleZoomOut} data-id="element-29" />
            </motion.div> : <motion.div key="energy-widgets" initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 50
        }} transition={{
          duration: 0.3
        }} className="w-[320px] flex-shrink-0 flex flex-col gap-3 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-y-auto" data-id="element-30">
              <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 px-1 mb-0.5" data-id="element-31">
                에너지 모니터
              </div>

              <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="flex-1 flex flex-col min-h-0" data-id="element-32">
                <EnergyChart data-id="element-33" />
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex-1 flex flex-col min-h-0" data-id="element-34">
                <MonthlyEnergyWidget data-id="element-35" />
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="flex-1 flex flex-col min-h-0" data-id="element-36">
                <TopOutletWidget data-id="element-37" />
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="flex-1 flex flex-col min-h-0" data-id="element-38">
                <OutletPowerChart data-id="element-39" />
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}